import { useCallback, useMemo } from 'react';
import { useMutation } from 'react-query';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { getClipReadOnlyPlayerConfig } from '@goldcast/player-config/src/utils/clipUtils';
import { BulkDownloadQueuedItem } from '@goldcast/api/content';
import { bulkDownloadContentClips } from '@/libs/clipContentUtil';
import useDialog from '@/components/organisms/useDialog';
import { downloadURI } from '@/libs/core';
import { showActionToast, showErrorToast } from '@/libs/toast/toast';
import { storeDownloadIdForSession } from '@/libs/downloadSessionUtil';
import { downloadsPopupOpen, loadContentDownloadsList } from '@/stores/downloadsStore';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { useAppContext } from '@/context/AppContext/AppContext';
import { savedAISearch } from '@/stores/savedAISearch';

export default function useBulkDownload({
  clips,
  selectedClipIds,
  exitMultiSelectMode
}: {
  clips: Clip[];
  selectedClipIds: Set<string>;
  exitMultiSelectMode: () => void;
}) {
  const { adminAppStore } = useAppContext();

  const {
    isOpen: isBulkDownloadDialogOpen,
    openDialog: openBulkDownloadDialog,
    closeDialog: closeBulkDownloadDialog
  } = useDialog();

  const { downloadableClips, nonDownloadableClips } = useMemo(
    () =>
      clips.reduce(
        (acc, clip) => {
          if (!selectedClipIds.has(clip.id)) {
            return acc;
          }
          if (getClipReadOnlyPlayerConfig(clip)) {
            acc.downloadableClips.push(clip);
          } else {
            acc.nonDownloadableClips.push(clip);
          }
          return acc;
        },
        { downloadableClips: [] as Clip[], nonDownloadableClips: [] as Clip[] }
      ),
    [clips, selectedClipIds]
  );

  async function handleQueuedItems(queued: BulkDownloadQueuedItem[]) {
    const doneItems = queued.filter(item => item.status === 'DONE' && item.download_url);
    const processingItems = queued.filter(item => item.status !== 'DONE');

    const contentId = downloadableClips[0]?.content?.id || '';
    const savedSearchId = savedAISearch.getSnapshot()?.id;

    doneItems.forEach(item => downloadURI(item.download_url!));

    if (doneItems.length > 0) {
      loadContentDownloadsList(contentId, savedSearchId);
    }

    if (processingItems.length > 0) {
      processingItems.forEach(item => storeDownloadIdForSession(item.download_id));

      await loadContentDownloadsList(contentId, savedSearchId);
      EventBus.dispatch(CustomEvents.CloseSharePopup);
      EventBus.dispatch(CustomEvents.DownloadTriggerSuccess);

      if (!downloadsPopupOpen.getSnapshot()) {
        showActionToast('success', {
          title: 'Processing Started',
          content: (
            <div>
              We'll send an email to <span className="tw-font-bold">{adminAppStore.user!.email}</span> when your
              downloads are ready.
            </div>
          )
        });
      }
    }
  }

  const { mutate: bulkDownloadClips, isLoading: isBulkDownloading } = useMutation({
    mutationFn: (clipIds: string[]) => bulkDownloadContentClips(clipIds),
    onSuccess: ({ queued, failed }) => {
      closeBulkDownloadDialog();
      if (queued.length > 0) {
        handleQueuedItems(queued);
      }
      if (failed.length > 0 && queued.length === 0) {
        showErrorToast('Download failed for all selected clips. Please try again.');
      } else if (failed.length > 0) {
        showErrorToast(`${failed.length} clip(s) could not be downloaded. Others are processing.`);
      }
      exitMultiSelectMode();
    },
    onError: () => {
      closeBulkDownloadDialog();
      showErrorToast('Failed to download clips. Please try again.');
    }
  });

  const onConfirmBulkDownload = useCallback(() => {
    bulkDownloadClips(downloadableClips.map(clip => clip.id));
  }, [downloadableClips, bulkDownloadClips]);

  const onBulkDownloadClips = useCallback(() => {
    if (downloadableClips.length === 0) {
      return;
    }

    // All selected clips are ready — skip the dialog
    if (nonDownloadableClips.length === 0) {
      bulkDownloadClips(downloadableClips.map(clip => clip.id));
      return;
    }

    // Mixed selection — show dialog so user knows what will be skipped
    openBulkDownloadDialog();
  }, [downloadableClips, nonDownloadableClips, bulkDownloadClips, openBulkDownloadDialog]);

  return {
    isBulkDownloading,
    isBulkDownloadDialogOpen,
    closeBulkDownloadDialog,
    onBulkDownloadClips,
    onConfirmBulkDownload,
    downloadableClips,
    nonDownloadableClips
  };
}
